import React, { useState } from 'react'
import { Button, Checkbox, Divider, Flex, Popover, Segmented, Typography } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { CalendarRange } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../../redux/store'
import { applyFiscalYearColumnPreferences } from '../../../redux/context/filters/actions'
import { filtersSelector } from '../../../redux/context/filters/selectors'
import { contextCompanyIdSelector } from '../../../redux/context/company/selectors'
import { SortOrder } from '../../../redux/context/filters/types'
import { periodGroupLabel } from '../../../redux/context/fiscalYears/utils'
import { useSelectedFiscalYears } from '../../fiscalYear/hooks/useSelectedFiscalYears'
import LucideIcon from '../../../shared/components/LucideIcon'
import PeriodTabFooter from './PeriodTabFooter'

const ContextFiscalYearSelect: React.FC = () => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()
  const [open, setOpen] = useState(false)

  const companyId = useSelector(contextCompanyIdSelector)
  // Only fiscal years that can produce a column are listed
  const { selectable, selectedSelectable, isAllSelected } = useSelectedFiscalYears()
  const { groupSortOrder = 'asc', monthSortOrder = 'asc' } = useSelector(filtersSelector)

  const isActive = !isAllSelected || groupSortOrder === 'desc' || monthSortOrder === 'desc'

  // Draft state: rebuilding columns is heavy, so changes are applied only from the Apply button
  const [draftIds, setDraftIds] = useState<number[]>([])
  const [draftGroupOrder, setDraftGroupOrder] = useState<SortOrder>(groupSortOrder)
  const [draftMonthOrder, setDraftMonthOrder] = useState<SortOrder>(monthSortOrder)

  const handleOpenChange = (visible: boolean) => {
    if (visible) {
      setDraftIds(selectedSelectable.map(fy => fy.id))
      setDraftGroupOrder(groupSortOrder)
      setDraftMonthOrder(monthSortOrder)
    }
    setOpen(visible)
  }

  const toggleFiscalYear = (id: number, checked: boolean) => {
    setDraftIds(ids => (checked ? [...ids, id] : ids.filter(selectedId => selectedId !== id)))
  }

  const apply = (ids: number[], groupOrder: SortOrder, monthOrder: SortOrder) => {
    if (companyId) {
      // All selected is stored as undefined so the selection keeps following the reportable fiscal years
      const fiscalYearIds = ids.length === selectable.length ? undefined : ids
      dispatch(applyFiscalYearColumnPreferences(companyId, fiscalYearIds, groupOrder, monthOrder))
    }
    setOpen(false)
  }

  const handleSubmit = () => apply(draftIds, draftGroupOrder, draftMonthOrder)

  const handleClear = () =>
    apply(
      selectable.map(fy => fy.id),
      'asc',
      'asc'
    )

  const sortOrderOptions = [
    { label: t('periodSelect:oldest-first'), value: 'asc' as SortOrder },
    { label: t('periodSelect:newest-first'), value: 'desc' as SortOrder }
  ]

  const content = (
    <div style={{ width: 280 }}>
      <Typography.Text strong style={{ display: 'block' }}>
        {t('periodSelect:fiscal-years-title')}
      </Typography.Text>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        {t('periodSelect:fiscal-years-hint')}
      </Typography.Text>
      <Flex vertical gap={4} style={{ maxHeight: 300, overflowY: 'auto' }}>
        {selectable.map(fiscalYear => {
          const checked = draftIds.includes(fiscalYear.id)
          return (
            <Checkbox
              key={fiscalYear.id}
              checked={checked}
              // The last selected fiscal year can't be removed
              disabled={checked && draftIds.length === 1}
              onChange={e => toggleFiscalYear(fiscalYear.id, e.target.checked)}
            >
              {periodGroupLabel(fiscalYear)}
            </Checkbox>
          )
        })}
      </Flex>
      <Divider style={{ margin: '12px 0' }} />
      <Typography.Text style={{ display: 'block', marginBottom: 4 }}>{t('periodSelect:order')}</Typography.Text>
      <Segmented<SortOrder> block value={draftGroupOrder} onChange={setDraftGroupOrder} options={sortOrderOptions} />
      <Typography.Text style={{ display: 'block', marginTop: 12, marginBottom: 4 }}>
        {t('periodSelect:month-order')}
      </Typography.Text>
      <Segmented<SortOrder> block value={draftMonthOrder} onChange={setDraftMonthOrder} options={sortOrderOptions} />
      <PeriodTabFooter onSubmit={handleSubmit} onClear={handleClear} />
    </div>
  )

  return (
    <Popover
      trigger="click"
      arrow={false}
      placement="bottomLeft"
      open={open}
      onOpenChange={handleOpenChange}
      content={content}
    >
      <Button
        data-cy="context-fiscal-year-select"
        className={isActive ? 'active' : undefined}
        icon={<LucideIcon icon={CalendarRange} />}
      >
        {t('periodSelect:fiscal-years')} ({selectedSelectable.length})
        <DownOutlined style={{ fontSize: 10, color: 'rgba(0, 0, 0, 0.25)' }} />
      </Button>
    </Popover>
  )
}

export default ContextFiscalYearSelect
